import {
  CampaignBulkActionCollection,
  IBrand,
  ICampaignBulkAction,
  ICampaignBulkActionPage,
  ICampaignEntryResetRules,
  ICustomReport,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IMarketingCampaignRunCost,
  IMarketingCampaignStep,
  IMarketingCampaignStepConversion,
  IPatient,
  MarketingCampaignCollection,
  MarketingCampaignEntryCollection,
  MarketingCampaignEntryStatus,
  MarketingCampaignScope,
  MarketingCampaignStatus,
  MarketingCampaignStepConversionStatus,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asReffable,
  asyncForEach,
  AtLeast,
  collectionGroupQuery,
  CollectionReference,
  CustomRecurrenceFrequency,
  DocumentReference,
  endsOnCount,
  endsOnDate,
  Firestore,
  FirestoreAggregate,
  IReffable,
  isSameRef,
  mergeDayAndTime,
  multiFilter,
  Query,
  QueryConstraint,
  safeCombineLatest,
  subCollection,
  Timestamp,
  toMoment,
  toMomentTz,
  toQuery,
  toTimestamp,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { TimezoneResolver } from '../../timezone';
import { AutomationTiming } from '../automation/automation-timing';
import { Brand } from '../brand';
import { CustomReport } from '../custom-report/custom-report';
import {
  getSeparationUnit,
  RecurrenceDirection,
} from '../schedule/recurrence-pattern';
import { MarketingCampaignStep } from './campaign-step';

const defaultFrequencyCount = 1;
const defaultFrequencyType = CustomRecurrenceFrequency.Daily;

export class MarketingCampaign {
  static init(
    overrides: AtLeast<
      IMarketingCampaign,
      'name' | 'type' | 'brandRef' | 'practiceRef'
    >
  ): IMarketingCampaign {
    return {
      status: MarketingCampaignStatus.Draft,
      steps: [],
      scope: TemplateScope.Patient,
      // No `reentryScope`: alongside `multipleEntry: false` it claims a
      // re-entry scope that never applies.
      entryResetRules: {
        multipleEntry: false,
        frequencyCount: defaultFrequencyCount,
        frequencyType: defaultFrequencyType,
        maxEntryCount: undefined,
      },
      deleted: false,
      dismissedCustomReportPrompt: false,
      ...overrides,
    };
  }

  static brandRef(
    campaign: IReffable<IMarketingCampaign>
  ): DocumentReference<IBrand> {
    return Firestore.getParentDocRef<IBrand>(campaign.ref);
  }

  static marketingCampaignRunCol(
    campaign: IReffable<IMarketingCampaign>
  ): CollectionReference<IMarketingCampaignRun> {
    return subCollection<IMarketingCampaignRun>(
      campaign.ref,
      MarketingCampaignCollection.MarketingCampaignRuns
    );
  }

  static bulkActionCol(
    campaign: IReffable<IMarketingCampaign>
  ): CollectionReference<ICampaignBulkAction> {
    return subCollection<ICampaignBulkAction>(
      campaign.ref,
      MarketingCampaignCollection.BulkActions
    );
  }

  static bulkActionPageCol(
    bulkAction: IReffable<ICampaignBulkAction>
  ): CollectionReference<ICampaignBulkActionPage> {
    return subCollection<ICampaignBulkActionPage>(
      bulkAction.ref,
      CampaignBulkActionCollection.Pages
    );
  }

  static marketingCampaignRuns$(
    campaign: IReffable<IMarketingCampaign>
  ): Observable<WithRef<IMarketingCampaignRun>[]> {
    return all$(
      undeletedQuery(MarketingCampaign.marketingCampaignRunCol(campaign))
    );
  }

  static marketingCampaignEntryCol(
    campaign: IReffable<IMarketingCampaign>
  ): CollectionReference<IMarketingCampaignEntry> {
    return subCollection<IMarketingCampaignEntry>(
      campaign.ref,
      MarketingCampaignCollection.MarketingCampaignEntries
    );
  }

  static entriesForPatientQuery(
    campaign: IReffable<IMarketingCampaign>,
    patientRef: DocumentReference<IPatient>
  ): Query<IMarketingCampaignEntry> {
    return undeletedQuery(
      MarketingCampaign.marketingCampaignEntryCol(campaign),
      where('patient.ref', '==', patientRef)
    );
  }

  static isRecurring(campaign: WithRef<IMarketingCampaign>): boolean {
    return !!campaign.recurrencePattern;
  }

  static async customReport(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<WithRef<ICustomReport> | undefined> {
    return campaign.customReportRef
      ? Firestore.getDoc(campaign.customReportRef)
      : undefined;
  }

  static async getEntriesCount(
    campaign: IReffable<IMarketingCampaign>,
    campaignRun?: IReffable<IMarketingCampaignRun>,
    status?: MarketingCampaignEntryStatus[]
  ): Promise<number> {
    const constraints: QueryConstraint[] = [
      where('campaignRef', '==', campaign.ref),
    ];

    if (campaignRun) {
      constraints.push(where('campaignRunRef', '==', campaignRun.ref));
    }
    if (status && status.length > 0) {
      constraints.push(where('status', 'in', status));
    }

    const entryCol = MarketingCampaign.marketingCampaignEntryCol(campaign);
    return Firestore.getCount(
      toQuery(undeletedQuery(entryCol), ...constraints)
    );
  }

  static stepConversionsQuery(
    campaign: IReffable<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Query {
    const constraints: QueryConstraint[] = [
      where('campaignRef', '==', campaign.ref),
    ];
    if (campaignRun) {
      constraints.push(where('campaignRunRef', '==', campaignRun.ref));
    }
    return undeletedQuery(
      collectionGroupQuery(MarketingCampaignEntryCollection.StepConversions)
    );
  }

  static async resolveCosts(
    campaign: IReffable<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Promise<IMarketingCampaignRunCost> {
    const constraints: QueryConstraint[] = [
      where('campaignRef', '==', campaign.ref),
    ];
    if (campaignRun) {
      constraints.push(where('campaignRunRef', '==', campaignRun.ref));
    }
    const stepConversionQuery = undeletedQuery(
      collectionGroupQuery<IMarketingCampaignStepConversion>(
        MarketingCampaignEntryCollection.StepConversions,
        ...constraints
      )
    );

    const statuses = [
      MarketingCampaignStepConversionStatus.Queued,
      MarketingCampaignStepConversionStatus.Pending,
    ];
    const estimatesQuery = toQuery(
      stepConversionQuery,
      where('status', 'in', statuses)
    );
    const estimates = await FirestoreAggregate.get(estimatesQuery, {
      estimatedCost: FirestoreAggregate.sum('estimatedCost'),
      estimatedCostRemaining: FirestoreAggregate.sum('estimatedCostRemaining'),
    });

    const incurredQuery = stepConversionQuery;
    const incurred = await FirestoreAggregate.get(incurredQuery, {
      stepCount: FirestoreAggregate.count(),
      incurredCost: FirestoreAggregate.sum('incurredCost'),
    });

    return {
      ...estimates,
      ...incurred,
    };
  }

  static marketingCampaignStepCol(
    campaign: IReffable<IMarketingCampaign>
  ): CollectionReference<IMarketingCampaignStep> {
    return subCollection<IMarketingCampaignStep>(
      campaign.ref,
      MarketingCampaignCollection.MarketingCampaignSteps
    );
  }

  static marketingCampaignSteps$(
    campaign: IReffable<IMarketingCampaign>
  ): Observable<WithRef<IMarketingCampaignStep>[]> {
    return all$(
      undeletedQuery(MarketingCampaign.marketingCampaignStepCol(campaign))
    );
  }

  static orderedSteps$(
    campaign: WithRef<IMarketingCampaign>
  ): Observable<WithRef<IMarketingCampaignStep>[]> {
    return safeCombineLatest(
      campaign.steps.map((step) => Firestore.doc$(step))
    ).pipe(multiFilter((step) => !step.deleted));
  }

  static async orderedSteps(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<WithRef<IMarketingCampaignStep>[]> {
    return (
      await asyncForEach(campaign.steps, (step) => Firestore.getDoc(step))
    ).filter((step) => !step.deleted);
  }

  static async addStep(
    campaign: WithRef<IMarketingCampaign>,
    step: IMarketingCampaignStep
  ): Promise<DocumentReference<IMarketingCampaignStep>> {
    const newStep = await Firestore.addDoc<IMarketingCampaignStep>(
      MarketingCampaign.marketingCampaignStepCol(campaign),
      step
    );
    await Firestore.patchDoc(campaign.ref, {
      steps: [...campaign.steps, newStep],
    });

    return newStep;
  }

  static async removeStep(
    campaign: WithRef<IMarketingCampaign>,
    stepRef: DocumentReference<IMarketingCampaignStep>
  ): Promise<void> {
    await Firestore.patchDoc(campaign.ref, {
      steps: campaign.steps.filter((step) => !isSameRef(step, stepRef)),
    });

    await Firestore.deleteDoc(stepRef);
  }

  static async shouldCompleteCampaign(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<boolean> {
    if (!campaign.recurrencePattern) {
      return false;
    }

    const completedCount = await Firestore.getCount(
      undeletedQuery(
        collectionGroupQuery(
          MarketingCampaignCollection.MarketingCampaignEntries,
          where('status', '==', MarketingCampaignEntryStatus.Completed),
          where('campaignRef', '==', campaign.ref)
        )
      )
    );

    if (
      endsOnCount(campaign.recurrencePattern) &&
      completedCount >= campaign.recurrencePattern.occurrenceCount
    ) {
      return true;
    }

    const endingDate = campaign.recurrencePattern.endingDate;
    if (
      endsOnDate(campaign.recurrencePattern) &&
      endingDate &&
      moment().isAfter(toMoment(endingDate))
    ) {
      return true;
    }

    return false;
  }

  static async completeCampaign(
    campaign: IReffable<IMarketingCampaign>
  ): Promise<void> {
    await Firestore.patchDoc(campaign.ref, {
      status: MarketingCampaignStatus.Completed,
    });
  }

  static async getNextRunDate(
    campaign: IMarketingCampaign,
    lastRunAt?: Timestamp
  ): Promise<Timestamp | undefined> {
    if (!campaign.recurrencePattern) {
      return;
    }

    const timezone = await TimezoneResolver.fromPracticeRef(
      campaign.practiceRef
    );

    const unit = getSeparationUnit(campaign.recurrencePattern.frequencyType);

    if (campaign.nextRunAt && toMoment(campaign.nextRunAt).isAfter(moment())) {
      return campaign.nextRunAt;
    }

    const initialDate = lastRunAt
      ? toMomentTz(lastRunAt, timezone)
      : campaign.nextRunAt
        ? toMomentTz(campaign.nextRunAt, timezone)
        : toMomentTz(
            campaign.recurrencePattern.startDate ?? moment(),
            timezone
          );

    const nextValidOccurrence = AutomationTiming.getNextValidOccurrence(
      initialDate,
      campaign.recurrencePattern.daysOfWeek,
      RecurrenceDirection.Forward,
      timezone,
      campaign.recurrencePattern.countOnlySelectedDays ?? false,
      unit,
      campaign.recurrencePattern.seperationCount
    );

    if (!nextValidOccurrence) {
      return;
    }

    if (!campaign.recurrenceRunTime) {
      return toTimestamp(nextValidOccurrence);
    }

    const time = mergeDayAndTime(
      nextValidOccurrence,
      campaign.recurrenceRunTime,
      timezone
    );

    return toTimestamp(time);
  }

  static async duplicate(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<DocumentReference<IMarketingCampaign>> {
    const newCustomReport = campaign.customReportRef
      ? CustomReport.duplicate(await Firestore.getDoc(campaign.customReportRef))
      : undefined;

    const customReportRef = newCustomReport
      ? await Firestore.addDoc(
          CustomReport.col(asReffable(campaign.brandRef)),
          newCustomReport
        )
      : undefined;

    const newStepRefs = await asyncForEach(campaign.steps, async (stepRef) => {
      const step = await Firestore.getDoc(stepRef);
      const newStep = MarketingCampaignStep.duplicate(step);
      return Firestore.addDoc(
        MarketingCampaign.marketingCampaignStepCol(campaign),
        newStep
      );
    });

    const newCampaign = MarketingCampaign.init({
      name: `${campaign.name} (Copy)`,
      status: MarketingCampaignStatus.Draft,
      recurrencePattern: campaign.recurrencePattern,
      recurrenceRunTime: campaign.recurrenceRunTime,
      type: campaign.type,
      scope: campaign.scope,
      entryResetRules: campaign.entryResetRules,
      customReportRef,
      brandRef: campaign.brandRef,
      practiceRef: campaign.practiceRef,
      conversionType: campaign.conversionType,
      maximumEntriesPerRun: campaign.maximumEntriesPerRun,
      steps: newStepRefs,
    });

    return Firestore.addDoc(
      Brand.marketingCampaignCol(asReffable(campaign.brandRef)),
      newCampaign
    );
  }

  static reentryScope(campaign: IMarketingCampaign): MarketingCampaignScope {
    return (
      campaign.entryResetRules?.reentryScope ??
      campaign.scope ??
      TemplateScope.Patient
    );
  }

  /**
   * Only `multipleEntry` enables re-entry, yet campaigns exist storing a
   * `reentryScope` with it off. Resolve before displaying re-entry settings so
   * those campaigns are not shown as configured.
   */
  static resolveEntryResetRules(
    campaign: IMarketingCampaign
  ): ICampaignEntryResetRules {
    const rules: ICampaignEntryResetRules | undefined =
      campaign.entryResetRules;
    const multipleEntry = rules?.multipleEntry === true;

    return {
      multipleEntry,
      reentryScope: multipleEntry
        ? MarketingCampaign.reentryScope(campaign)
        : undefined,
      frequencyCount: rules?.frequencyCount ?? defaultFrequencyCount,
      frequencyType: rules?.frequencyType ?? defaultFrequencyType,
      maxEntryCount: rules?.maxEntryCount,
    };
  }

  /**
   * The engine reads only `multipleEntry`, and nothing else keeps it in step
   * with the chosen scope. Route every re-entry write through here.
   */
  static normaliseEntryResetRules(
    rules: ICampaignEntryResetRules
  ): ICampaignEntryResetRules {
    return { ...rules, multipleEntry: rules.reentryScope !== undefined };
  }
}
